import React, { useState, useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { useAuth } from '../contexts/AuthContext'
import { Input, Layout } from '../../../shared/components'

type UserType = 'physical' | 'legal' | null

interface RegisterFormData {
  name: string
  email: string
  cpf: string
  cnpj: string
  password: string
  confirmPassword: string
  phone: string
  birthDate: string
}

const formatCpf = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

const formatCnpj = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

const formatPhone = (value: string) => {
  return value
    .replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
    .replace(/(-\d{4})\d+?$/, '$1')
}

const Register: React.FC = () => {
  const [userType, setUserType] = useState<UserType>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { register: registerUser, error: authError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset
  } = useForm<RegisterFormData>({
    defaultValues: {
      name: '',
      email: '',
      cpf: '',
      cnpj: '',
      password: '',
      confirmPassword: '',
      phone: '',
      birthDate: ''
    }
  })

  const watchedPassword = watch('password')

  // Pegar a rota de redirecionamento após cadastro
  const redirectTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const redirectParam = searchParams.get('redirect')
    
    if (redirectParam && redirectParam.startsWith('/')) {
      return redirectParam
    }
    
    return location.state?.from?.pathname || '/conta'
  }, [location.search, location.state])

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true)
    setError('')

    try {
      const document = userType === 'physical' ? data.cpf : data.cnpj
      
      // Converter data para formato ISO datetime (exigido pelo backend)
      let birthDateISO: string | undefined = undefined
      if (data.birthDate) {
        birthDateISO = `${data.birthDate}T00:00:00Z`
      }
      
      const success = await registerUser({
        name: data.name,
        email: data.email,
        cpf: document,
        password: data.password,
        phone: data.phone,
        birthDate: birthDateISO
      })

      if (success) {
        navigate(redirectTo, { replace: true })
      } else {
        setError(authError || 'Erro ao criar conta. Tente novamente.')
      }
    } catch {
      setError(authError || 'Erro ao criar conta. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUserTypeChange = (type: UserType) => {
    setUserType(type)
    reset()
    setError('')
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl w-full bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col md:flex-row">
          
          {/* Lado Esquerdo - Imagem Decorativa */}
          <div className="w-full md:w-5/12 bg-blue-900 relative hidden md:block">
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop" 
              alt="BaterPower Registro" 
              className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 to-indigo-900/90"></div>
            
            <div className="relative z-10 flex flex-col items-center justify-center h-full text-white px-12 text-center p-12">
              <div className="mb-8 p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <h2 className="text-3xl font-bold mb-6 font-display">Deixe-nos conhecer você melhor</h2>
              <p className="text-blue-100 text-lg leading-relaxed">
                Crie sua conta hoje e tenha acesso a preços exclusivos, histórico de pedidos e suporte especializado em energia.
              </p>
              
              <div className="mt-12 grid grid-cols-2 gap-6 w-full max-w-sm text-left">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-300 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-blue-100">Entrega rápida</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-300 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-blue-100">Garantia com fabricante</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-300 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-blue-100">Suporte técnico</span>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-blue-300 mr-3 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-sm text-blue-100">Preços acessíveis</span>
                </div>
              </div>
            </div>
          </div>

          {/* Lado Direito - Conteúdo */}
          <div className="w-full md:w-7/12 p-8 md:p-12 lg:p-16">
            <div className="text-center md:text-left mb-10">
              <h2 className="text-3xl font-bold text-gray-900 font-display">
                {!userType ? 'Criar nova conta' : userType === 'legal' ? 'Cadastro Empresarial' : 'Cadastro Pessoal'}
              </h2>
              <p className="mt-3 text-base text-gray-600">
                {!userType 
                  ? 'Escolha como você deseja se cadastrar para começar.'
                  : 'Preencha os campos abaixo para concluir seu cadastro.'}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center shadow-sm">
                <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* User Type Selection */}
            {!userType && (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => handleUserTypeChange('physical')}
                  className="w-full flex items-center justify-between p-6 border-2 border-gray-100 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer group shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-5">
                    <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                        Pessoa Física
                      </h3>
                      <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">
                        Conta pessoal para compras individuais
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-full p-1 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => handleUserTypeChange('legal')}
                  className="w-full flex items-center justify-between p-6 border-2 border-gray-100 rounded-xl hover:border-blue-500 hover:bg-blue-50/50 transition-all cursor-pointer group relative shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center space-x-5">
                    <div className="flex-shrink-0 p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                        Pessoa Jurídica
                      </h3>
                      <p className="text-sm text-gray-500 group-hover:text-blue-600/80 transition-colors">
                        Conta empresarial com condições especiais
                      </p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-full p-1 group-hover:bg-blue-200 transition-colors">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Info Box for Legal Entity */}
                <div className="mt-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start">
                    <div className="p-2 bg-blue-100 rounded-lg mr-4 flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="text-sm text-blue-900">
                      <p className="font-bold mb-2 text-base">Vantagens exclusivas para empresas:</p>
                      <ul className="space-y-2">
                        <li className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Condições especiais
                        </li>
                        <li className="flex items-center">
                          <svg className="w-4 h-4 mr-2 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Consultoria personalizada
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                  <p className="text-sm text-gray-600">
                    Já tem uma conta?{' '}
                    <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700 hover:underline transition-colors">
                      Fazer login
                    </Link>
                  </p>
                </div>
              </div>
            )}

            {/* Register Form */}
            {userType && (
              <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex items-center justify-between mb-6">
                  <button
                    type="button"
                    onClick={() => handleUserTypeChange(null)}
                    className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors group"
                  >
                    <div className="mr-2 p-1 rounded-full group-hover:bg-blue-50 transition-colors">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </div>
                    Voltar para seleção
                  </button>
                  <span className="text-xs font-semibold px-2 py-1 bg-gray-100 text-gray-600 rounded">
                    {userType === 'legal' ? 'PESSOA JURÍDICA' : 'PESSOA FÍSICA'}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {userType === 'legal' ? 'Razão Social' : 'Nome Completo'}
                    </label>
                    <Input
                      id="name"
                      type="text"
                      {...register('name', {
                        required: userType === 'legal' ? 'Razão social é obrigatória' : 'Nome completo é obrigatório',
                        minLength: { value: 2, message: 'Nome deve ter no mínimo 2 caracteres' }
                      })}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                        errors.name ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder={userType === 'legal' ? 'Razão social da empresa' : 'Seu nome completo'}
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email', {
                        required: 'Email é obrigatório',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Email inválido'
                        }
                      })}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                        errors.email ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="seu@email.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor={userType === 'physical' ? 'cpf' : 'cnpj'} className="block text-sm font-medium text-gray-700 mb-2">
                      {userType === 'physical' ? 'CPF' : 'CNPJ'}
                    </label>
                    <Controller
                      name={userType === 'physical' ? 'cpf' : 'cnpj'}
                      control={control}
                      rules={{
                        required: userType === 'physical' ? 'CPF é obrigatório' : 'CNPJ é obrigatório'
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            id={userType === 'physical' ? 'cpf' : 'cnpj'}
                            type="text"
                            value={field.value}
                            onChange={(e) => {
                              const formatted = userType === 'physical' 
                                ? formatCpf(e.target.value)
                                : formatCnpj(e.target.value)
                              field.onChange(formatted)
                            }}
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                              errors[userType === 'physical' ? 'cpf' : 'cnpj'] ? 'border-red-300' : 'border-gray-200'
                            }`}
                            placeholder={userType === 'physical' ? '000.000.000-00' : '00.000.000/0000-00'}
                            maxLength={userType === 'physical' ? 14 : 18}
                          />
                          {errors[userType === 'physical' ? 'cpf' : 'cnpj'] && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors[userType === 'physical' ? 'cpf' : 'cnpj']?.message}
                            </p>
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone
                    </label>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{
                        required: userType === 'physical' ? 'Telefone é obrigatório' : false
                      }}
                      render={({ field }) => (
                        <>
                          <Input
                            id="phone"
                            type="text"
                            value={field.value}
                            onChange={(e) => {
                              field.onChange(formatPhone(e.target.value))
                            }}
                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                              errors.phone ? 'border-red-300' : 'border-gray-200'
                            }`}
                            placeholder="(00) 00000-0000"
                            maxLength={15}
                          />
                          {errors.phone && (
                            <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                          )}
                        </>
                      )}
                    />
                  </div>

                  {userType === 'physical' && (
                    <div className="md:col-span-2">
                      <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700 mb-2">
                        Data de Nascimento (Opcional)
                      </label>
                      <Controller
                        name="birthDate"
                        control={control}
                        render={({ field }) => (
                          <Input
                            id="birthDate"
                            type="date"
                            value={field.value || ''}
                            onChange={(event) => field.onChange(event.target.value)}
                            className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 border-gray-200"
                          />
                        )}
                      />
                    </div>
                  )}

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Senha
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        {...register('password', {
                          required: 'Senha é obrigatória',
                          minLength: { value: 8, message: 'Senha deve ter no mínimo 8 caracteres' }
                        })}
                        className={`w-full px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                          errors.password ? 'border-red-300' : 'border-gray-200'
                        }`}
                        placeholder="Mínimo 8 caracteres"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-blue-600 text-gray-400 transition-colors"
                      >
                        {showPassword ? (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                      Confirmar Senha
                    </label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      {...register('confirmPassword', {
                        required: 'Confirmação de senha é obrigatória',
                        validate: (value) => value === watchedPassword || 'As senhas não coincidem'
                      })}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-gray-50 focus:bg-white text-gray-900 placeholder-gray-400 ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="Repita a senha"
                    />
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                    )}
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-blue-500/30 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 hover:shadow-blue-600/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transition-all transform hover:-translate-y-0.5"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processando cadastro...
                      </div>
                    ) : (
                      'Finalizar Cadastro'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default Register
